///
/// EarthBrowser
///
/// Created by Matt Giger on 11/13/2023
/// Copyright (c) 2023 Geodata Labs
///

import { clamp } from "./math"

/**
 * Creates a color object from RGBA values.
 * @param rvalue - If only this parameter is provided, it's treated as a 32-bit RGBA value.
 *                 Otherwise, it represents the red component (0-1).
 * @param g - The green component (0-1). Optional.
 * @param b - The blue component (0-1). Optional.
 * @param a - The alpha component (0-1). Optional.
 * @returns A new color object.
 */
export function rgba(rvalue: number, g?: number, b?: number, a?: number): color {
	if(g === undefined || b === undefined || a == undefined) {
		// If only rvalue is provided, treat it as a 32-bit RGBA value
		return new color(
			hex8Bit(rvalue, 3) / 0xff,
			hex8Bit(rvalue, 2) / 0xff,
			hex8Bit(rvalue, 1) / 0xff,
			hex8Bit(rvalue, 0) / 0xff
		)
	} else if(a === undefined) {
		if(rvalue > 1 || g > 1 || b > 1) {
			rvalue = rvalue / 255
			g = g / 255
			b = b / 255
		}
		return new color(rvalue, g, b, 1)
	} else {
		if(rvalue > 1 || g > 1 || b > 1) {
			rvalue = rvalue / 255
			g = g / 255
			b = b / 255
		}
		if(a > 1) {
			a = a / 255
		}
		return new color(rvalue, g, b, a)
	}
}

/**
 * Creates a color object from HSL values.
 * @param h - Hue in degrees (0-360)
 * @param s - Saturation percentage (0-100)
 * @param l - Lightness percentage (0-100)
 * @param a - Optional alpha value (0-1). Defaults to 1.
 * @returns A new color object.
 */
export function hsla(h: number, s: number, l: number, a: number = 1): color {
	// Normalize values
	h = ((h % 360) + 360) % 360  // Ensure hue is 0-360
	s = clamp(s, 0, 100) / 100   // Convert to 0-1
	l = clamp(l, 0, 100) / 100   // Convert to 0-1
	a = clamp(a, 0, 1)

	const c = (1 - Math.abs(2 * l - 1)) * s
	const x = c * (1 - Math.abs((h / 60) % 2 - 1))
	const m = l - c / 2

	let r = 0, g = 0, b = 0

	if (h >= 0 && h < 60) {
		[r, g, b] = [c, x, 0]
	} else if (h >= 60 && h < 120) {
		[r, g, b] = [x, c, 0]
	} else if (h >= 120 && h < 180) {
		[r, g, b] = [0, c, x]
	} else if (h >= 180 && h < 240) {
		[r, g, b] = [0, x, c]
	} else if (h >= 240 && h < 300) {
		[r, g, b] = [x, 0, c]
	} else {
		[r, g, b] = [c, 0, x]
	}

	return new color(r + m, g + m, b + m, a)
}

/**
 * Represents a color with red, green, blue, and alpha components.
 */
export class color {
	/** The red component of the color (0-1) */
	readonly r: number
	/** The green component of the color (0-1) */
	readonly g: number
	/** The blue component of the color (0-1) */
	readonly b: number
	/** The alpha component of the color (0-1) */
	readonly a: number

	/**
	 * Creates a new color instance.
	 * @param r - The red component (0-1). Defaults to 0.
	 * @param g - The green component (0-1).
	 * @param b - The blue component (0-1).
	 * @param a - The alpha component (0-1).
	 */
	constructor(r: number, g: number, b: number, a: number) {
		this.r = clamp(r, 0, 1)
		this.g = clamp(g, 0, 1)
		this.b = clamp(b, 0, 1)
		this.a = clamp(a, 0, 1)
	}

	/**
	 * Converts the color to a HSV color.
	 * @returns A new hsv instance.
	 */
	public get hsv(): hsv {
		const max = Math.max(this.r, this.g, this.b)
		const min = Math.min(this.r, this.g, this.b)
		const delta = max - min

		// Calculate hue (0-1)
		let h = 0
		if (delta === 0) {
			h = 0
		} else if (max === this.r) {
			h = ((this.g - this.b) / delta) % 6
			if (h < 0) h += 6
		} else if (max === this.g) {
			h = (this.b - this.r) / delta + 2
		} else { // max === this.b
			h = (this.r - this.g) / delta + 4
		}
		h = h / 6 // Convert to 0-1 range

		// Calculate saturation (0-1)
		const s = max === 0 ? 0 : delta / max

		// Value is simply the max (0-1)
		const v = max

		return new hsv(h, s, v, this.a)
	}

	/**
	 * Converts the color to a hexadecimal string representation.
	 * @returns A string in the format "#RRGGBBAA".
	 */
	public toString(): string {
		let result = '#'
		const rv = Math.floor(this.r * 0xff)
		const gv = Math.floor(this.g * 0xff)
		const bv = Math.floor(this.b * 0xff)
		const av = Math.floor(this.a * 0xff)
		result += (rv >> 4).toString(16)
		result += (rv & 0xf).toString(16)
		result += (gv >> 4).toString(16)
		result += (gv & 0xf).toString(16)
		result += (bv >> 4).toString(16)
		result += (bv & 0xf).toString(16)
		result += (av >> 4).toString(16)
		result += (av & 0xf).toString(16)
		return result
	}
}

/**
 * Represents a color in HSV (Hue, Saturation, Value) color space.
 */
export class hsv {
	/** The hue component of the color (0-1) */
	public h: number
	/** The saturation component of the color (0-1) */
	public s: number
	/** The value component of the color (0-1) */
	public v: number
	/** The alpha component of the color (0-1) */
	public a: number

	/**
	 * Creates a new hsv color instance.
	 * @param h - The hue component of the color (0-1)
	 * @param s - The saturation component of the color (0-1)
	 * @param v - The value component of the color (0-1)
	 * @param a - The alpha component of the color (0-1)
	 */
	constructor(h: number, s: number, v: number, a: number = 1) {
		this.h = h
		this.s = s
		this.v = v
		this.a = a
	}

	/**
	 * Converts the HSV color to a RGB color.
	 * @returns A new color instance.
	 */
	public get color(): color {
		const h = (this.h * 360) % 360
		const c = this.v * this.s
		const x = c * (1 - Math.abs((h / 60) % 2 - 1))
		const m = this.v - c

		let r = 0, g = 0, b = 0

		if(h >= 0 && h < 60) {
			[r, g, b] = [c, x, 0]
		} else if (h >= 60 && h < 120) {
			[r, g, b] = [x, c, 0]
		} else if (h >= 120 && h < 180) {
			[r, g, b] = [0, c, x]
		} else if (h >= 180 && h < 240) {
			[r, g, b] = [0, x, c]
		} else if (h >= 240 && h < 300) {
			[r, g, b] = [x, 0, c]
		} else {
			[r, g, b] = [c, 0, x]
		}

		return new color(r + m, g + m, b + m, this.a)
	}
}

export const black = new color(0, 0, 0, 1)
export const white = new color(1, 1, 1, 1)
export const gray = new color(0.5, 0.5, 0.5, 1)
export const yellow = new color(1, 1, 0, 1)
export const purple = new color(1, 0, 1, 1)
export const red = new color(1, 0, 0, 1)
export const green = new color(0, 1, 0, 1)
export const blue = new color(0, 0, 1, 1)
export const transparent = new color(0, 0, 0, 0)

// Get the nth 8 bit element counting from the right
// like hex8Bit(0xCCDDFF00, 1) == 0xFF
function hex8Bit(value: number, index: number): number {
	return (value >> (index * 8)) & 0xff
}

export function lerpColor(a: color, b: color, t: number): color {
	return new color(
		a.r + (b.r - a.r) * t,
		a.g + (b.g - a.g) * t,
		a.b + (b.b - a.b) * t,
		a.a + (b.a - a.a) * t
	)
}